// src/components/AnalyticsDashboard.js
import React, { useEffect, useState, useRef } from 'react';
import './AnalyticsDashboard.css'; // <- separate CSS

export default function AnalyticsDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');
  // --- DOCS: modal/drawer state ---
const [docsOpen, setDocsOpen] = useState(false);
const [docsClient, setDocsClient] = useState(null); // { id, name, email }

const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'docs'

  // Digest state
  const [draft, setDraft] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [allUsers, setAllUsers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // --- Email Client Composer state ---
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeUserId, setComposeUserId] = useState(null);
  const [sendingClientEmail, setSendingClientEmail] = useState(false);
  const [clientFilter, setClientFilter] = useState('');


  // NEW: collapse state for big client table
  const [clientCasesCollapsed, setClientCasesCollapsed] = useState(false);

  // helper to get API base
  const apiBase = React.useMemo(()=>{
    const raw = process.env.REACT_APP_API_URL || '';
    const trimmed = raw.replace(/\/$/, '');
    const root = trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
    return `${root}/admincontrol`;
  },[]);

  // debounce helper (same behavior)
  const debounce = (fn, ms=500) => {
    let t; return (...args)=>{ clearTimeout(t); t=setTimeout(()=>fn(...args), ms); };
  };

  // autosave draft whenever it changes (kept as callable function)
  const autosave = React.useRef(debounce(async (d)=>{
    try{
      await fetch(`${apiBase}/digest/draft`,{
        method:'PUT',
        headers:{'Content-Type':'application/json'},
        credentials:'include',
        body: JSON.stringify(d)
      });
    }catch(e){ console.error('autosave failed', e); }
  }, 600)).current;

  // FIX: make saveClientField a real ref that ALWAYS holds a function
  const saveClientField = useRef(() => {}); // no-op at start
  useEffect(() => {
    const debounced = debounce(async (userId, patch)=>{
      try{
        await fetch(`${apiBase}/clients/${userId}`,{
          method:'PATCH',
          headers:{ 'Content-Type':'application/json' },
          credentials:'include',
          body: JSON.stringify(patch)
        });
      }catch(e){ console.error('client save failed', e); }
    }, 700);
    saveClientField.current = debounced;
    return () => {
      // keep it callable even after unmount to avoid "not a function"
      saveClientField.current = () => {};
    };
  }, [apiBase, debounce]);

  // helper: update client in local state optimistically
  const updateClientLocal = (id, patch) => {
    setClients(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  };

  // helper: yyyy-mm-dd for <input type="date">
  const toYMD = (v) => {
    try {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d)) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch { return ''; }
  };

  // protect route
  useEffect(() => {
    if (!user || user.role !== 'admin') setError('Not authorized');
    else setError('');
  }, [user]);

  // load analytics + clients + draft headers
  useEffect(() => {
    if (!user || user.role !== 'admin') return;

    fetch(`${apiBase}/website`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setStats)
      .catch(e => { console.error(e); setError('Unable to load analytics'); });

fetch(`${apiBase}/clients?includeDocCounts=1`, { credentials: 'include' })
  .then(r => r.ok ? r.json() : Promise.reject(r))
  .then(list => {
    // normalize defaults from file-backed meta when DB columns are missing
    const normalized = (list || []).map(c => ({
      ...c,
      documentsCount: typeof c.documentsCount === 'number' ? c.documentsCount : 0,
      hasDocuments: typeof c.hasDocuments === 'boolean' ? c.hasDocuments : (c.documentsCount || 0) > 0,
    }));
    setClients(normalized);
  })
  .catch(e => { console.error(e); setClients([]); });


    fetch(`${apiBase}/digest/draft`, { credentials: 'include' })
      .then(r => r.json())
      .then(setDraft)
      .catch(()=> setDraft({ recipients:null, converted:[], pipeline:[], leads:[] }));
  }, [user, apiBase]);

  // Lazy load users once, then filter in memory
  const runSearch = async () => {
    if (!q.trim()) { setResults([]); return; }
    if (!allUsers) {
      setLoadingUsers(true);
      try {
        const r = await fetch(`${apiBase}/digest/all-users`, { credentials:'include' });
        const list = await r.json();
        setAllUsers(list);
        setResults(filterLocal(list, q));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingUsers(false);
      }
    } else {
      setResults(filterLocal(allUsers, q));
    }
  };

  const filterLocal = (list, text) => {
    const t = text.toLowerCase();
    return list.filter(u =>
      (`${u.firstName||''} ${u.lastName||''} ${u.email||''}`).toLowerCase().includes(t)
    ).slice(0, 40); // cap visible results
  };

  const addTo = (section, u) => {
    setDraft(d => {
      const nd = { ...d, [section]: [...d[section], { userId: u.id, stage:'', lastFollowUp:'', notes:'' }] };
      autosave(nd);
      return nd;
    });
  };

  const updateItem = (section, idx, patch) => {
    setDraft(d => {
      const arr = d[section].slice();
      arr[idx] = { ...arr[idx], ...patch };
      const nd = { ...d, [section]: arr };
      autosave(nd);
      return nd;
    });
  };

  const removeItem = (section, idx) => {
    setDraft(d => {
      const arr = d[section].slice();
      arr.splice(idx, 1);
      const nd = { ...d, [section]: arr };
      autosave(nd);
      return nd;
    });
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/digest/draft`, {
        method:'PUT',
        headers:{ 'Content-Type':'application/json' },
        credentials:'include',
        body: JSON.stringify(draft),
      });
      setDraft(await res.json());
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await fetch(`${apiBase}/digest/send-now`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        credentials:'include',
        body: JSON.stringify(draft)
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Send failed');
      alert('Digest sent!');
    } catch (e) {
      alert(e.message);
    } finally {
      setSending(false);
    }
  };

  const openComposeForUser = (u) => {
    setComposeUserId(u.id);
    setComposeTo(u.email || '');
    setComposeSubject('');
    setComposeBody('');
    setComposeOpen(true);
  };

  if (error) return <div className="ae-error-banner">{error}</div>;
  if (!stats || !draft) return <div className="ae-loading">Loading…</div>;

  const Section = ({ title, name }) => (
    <div className="ae-card">
      <h3>{title}</h3>
      <table className="ae-table">
        <thead>
          <tr>
            <th>User ID</th>
            <th>Stage</th>
            <th>Last Follow-up</th>
            <th>Notes</th>
            <th></th>
          </tr>
        </thead>
       <tbody>
  {draft[name].length === 0 ? (
    <tr><td colSpan="5" className="ae-muted">No entries yet.</td></tr>
  ) : draft[name].map((x, i) => (
    <tr key={i}>
      <td data-label="User ID">#{x.userId}</td>

      <td data-label="Stage">
        <input
          autoComplete="off"
          defaultValue={x.stage || ''}
          onBlur={e => {
            const v = e.target.value.trim();
            updateItem(name, i, { stage: v });
            const fn = saveClientField.current;
            if (typeof fn === 'function') fn(x.userId, { pipelineStage: v || null });
          }}
          placeholder="Won / Negotiation / New"
        />
      </td>

      <td data-label="Last Follow-up">
        <input
          type="date"
          defaultValue={typeof x.lastFollowUp === 'string' ? x.lastFollowUp : ''}
          onBlur={e => {
            const v = e.target.value || null;
            updateItem(name, i, { lastFollowUp: v || '' });
            const fn = saveClientField.current;
            if (typeof fn === 'function') fn(x.userId, { lastFollowUp: v });
          }}
        />
      </td>

      <td data-label="Notes">
        <input
          autoComplete="off"
          defaultValue={x.notes || ''}
          onBlur={e => {
            const v = e.target.value.trim();
            updateItem(name, i, { notes: v });
            const fn = saveClientField.current;
            if (typeof fn === 'function') fn(x.userId, { followUpNotes: v || null });
          }}
          placeholder="Short note"
        />
      </td>

      <td data-label="Actions" className="ae-right">
        <button className="ae-btn ae-ghost" onClick={()=>removeItem(name,i)}>Remove</button>
      </td>
    </tr>
  ))}
</tbody>

      </table>
    </div>
  );
// --- DOCS: tiny util ---
const fmtSize = (n=0) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024*1024) return `${Math.round(n/1024)} KB`;
  return `${(n/1024/1024).toFixed(1)} MB`;
};
function DocumentsCenter({ apiBase, openModalFor }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  

  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const inputRef = useRef(null);

  const fmtSize = (n=0) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024*1024) return `${Math.round(n/1024)} KB`;
    return `${(n/1024/1024).toFixed(1)} MB`;
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
    const r = await fetch(`${apiBase}/clients?withDocs=1&includeDocCounts=1`, { credentials: 'include' });
const j = await r.json();
const normalized = (j || []).map(c => ({
  ...c,
  documentsCount: typeof c.documentsCount === 'number' ? c.documentsCount : 0,
  hasDocuments: typeof c.hasDocuments === 'boolean' ? c.hasDocuments : (c.documentsCount || 0) > 0,
}));
// If you truly want only clients with docs in this center:
const onlyWithDocs = normalized.filter(c => c.hasDocuments);
setList(onlyWithDocs);
setSelected(onlyWithDocs[0] || null);

      } catch (e) {
        setError(e.message || 'Load failed');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase]);

const loadDocs = async (clientId) => {
  const r = await fetch(`${apiBase}/clients/${clientId}/documents`, { credentials:'include' });
  const j = await r.json();
  // backend now returns { docs:[...], documentsCount, ... }
  const docsArr = Array.isArray(j) ? j : (j.docs || []);
  setDocs(docsArr);

  // also reflect count into the left sidebar list + selected
  const newCount = (Array.isArray(j) ? j.length : j.documentsCount) || docsArr.length || 0;
  setList(prev => prev.map(x => x.id === clientId ? { ...x, documentsCount: newCount, hasDocuments: newCount > 0 } : x));
  setSelected(prev => prev && prev.id === clientId ? { ...prev, documentsCount: newCount, hasDocuments: newCount > 0 } : prev);
};

  useEffect(() => {
    if (selected) loadDocs(selected.id);
  }, [selected]);

  const upload = async (e) => {
    e.preventDefault();
    if (!selected || !inputRef.current?.files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of inputRef.current.files) fd.append('files', f);
      fd.append('meta', JSON.stringify({ title, notes }));
     const resp = await fetch(`${apiBase}/clients/${selected.id}/documents`, { method:'POST', body: fd, credentials:'include' });
const data = await resp.json();
setTitle(''); setNotes(''); if (inputRef.current) inputRef.current.value = '';
// Update count from API right away
const newCount = typeof data.documentsCount === 'number' ? data.documentsCount : undefined;
if (typeof newCount === 'number') {
  setList(prev => prev.map(x => x.id === selected.id ? { ...x, documentsCount: newCount, hasDocuments: newCount > 0 } : x));
  setSelected(prev => prev ? { ...prev, documentsCount: newCount, hasDocuments: newCount > 0 } : prev);
}
// reload the docs list for fresh records
await loadDocs(selected.id);

    } finally { setBusy(false); }
  };

  const del = async (docId) => {
    if (!selected) return;
    if (!window.confirm('Delete this document?')) return;
    setBusy(true);
    try {
      const resp = await fetch(`${apiBase}/clients/${selected.id}/documents/${docId}`, { method:'DELETE', credentials:'include' });
const data = await resp.json();
// Update count first
const newCount = typeof data.documentsCount === 'number' ? data.documentsCount : undefined;
if (typeof newCount === 'number') {
  setList(prev => prev.map(x => x.id === selected.id ? { ...x, documentsCount: newCount, hasDocuments: newCount > 0 } : x));
  setSelected(prev => prev ? { ...prev, documentsCount: newCount, hasDocuments: newCount > 0 } : prev);
}
await loadDocs(selected.id);

    } finally { setBusy(false); }
  };

  const saveMeta = async (docId, patch) => {
    setBusy(true);
    try {
      await fetch(`${apiBase}/clients/${selected.id}/documents/${docId}`, {
        method:'PATCH',
        headers:{ 'Content-Type':'application/json' },
        credentials:'include',
        body: JSON.stringify(patch),
      });
      await loadDocs(selected.id);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="ae-card">Loading documents…</div>;
  if (error)   return <div className="ae-error-banner">{error}</div>;
  if (!list.length) return <div className="ae-card">No clients with documents yet.</div>;

  return (
    <div className="ae-docs-center">
      <aside className="ae-docs-sidebar">
        <div className="ae-docs-sidebar-head">
          <h3>Clients with Documents</h3>
          <div className="ae-muted">{list.length} total</div>
        </div>
        <div className="ae-docs-search">
          <input
            placeholder="Filter by name/email…"
            onChange={(e)=>{
              const t = e.target.value.toLowerCase();
              const rows = document.querySelectorAll('.ae-docs-item');
              rows.forEach(el=>{
                const v = (el.getAttribute('data-text')||'').toLowerCase();
                el.style.display = v.includes(t) ? '' : 'none';
              });
            }}
          />
        </div>
        <div className="ae-docs-listing">
          {list.map(c => {
            const label = `${c.firstName||''} ${c.lastName||''}`.trim() || c.email || `#${c.id}`;
            const text = `${label} ${c.email||''}`;
            const active = selected && selected.id === c.id;
            return (
              <div
                key={c.id}
                className={`ae-docs-item ${active?'is-active':''}`}
                data-text={text}
                onClick={()=>setSelected(c)}
              >
                <div className="ae-docs-item-title">{label}</div>
                <div className="ae-docs-item-sub ae-muted">
                  {c.email || '—'} · {c.documentsCount||0} file{(c.documentsCount||0)===1?'':'s'}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      <main className="ae-docs-main">
        {!selected ? (
          <div className="ae-card">Select a client to manage documents.</div>
        ) : (
          <div className="ae-card">
            <div className="ae-docs-main-head">
              <div>
                <h3 style={{margin:'0 0 4px'}}>
                  #{selected.id} — {(selected.firstName||'') + ' ' + (selected.lastName||'')}
                </h3>
                <div className="ae-muted">{selected.email || '—'}</div>
              </div>
              <div className="ae-docs-main-actions">
                <button
                  className="ae-btn"
                  onClick={()=>openModalFor({
                    id: selected.id,
                    name: `${selected.firstName||''} ${selected.lastName||''}`.trim(),
                    email: selected.email
                  })}
                >
                  Open as Modal
                </button>
              </div>
            </div>

            {/* Upload */}
            <form className="ae-docs-upload" onSubmit={upload}>
              <input ref={inputRef} type="file" multiple />
              <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Title (optional, applied to all)" />
              <input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Notes (optional, applied to all)" />
              <button className="ae-btn ae-primary" disabled={busy} type="submit">
                {busy ? 'Uploading…' : 'Upload'}
              </button>
            </form>

            {/* Docs list */}
            {docs.length === 0 ? (
              <div className="ae-muted">No documents yet for this client.</div>
            ) : (
              <div className="ae-docs-list">
                {docs.map(d => (
                  <div className="ae-doc-row" key={d.id}>
                    <div className="ae-doc-main">
                      <a className="ae-doc-title" href={`${apiBase}/clients/${selected.id}/documents/${d.id}/download`} title="Download">
                        {d.title || d.originalName}
                      </a>
                      <div className="ae-doc-meta ae-muted">
                        {fmtSize(d.size)} · {new Date(d.uploadedAt).toLocaleString()} · {d.mime || 'file'}
                      </div>
                      <div className="ae-doc-notes">
                        <span className="ae-muted">Notes:</span>{' '}
                        <span
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={e=>saveMeta(d.id, { notes: e.currentTarget.textContent })}
                        >
                          {d.notes || 'Add notes…'}
                        </span>
                      </div>
                    </div>
                    <div className="ae-doc-actions">
                      <button className="ae-btn ae-ghost"
                        onClick={()=>{
                          const t = prompt('Title', d.title || d.originalName || '');
                          if (t !== null) saveMeta(d.id, { title: t });
                        }}>Rename</button>
                      <button className="ae-btn ae-ghost" onClick={()=>del(d.id)}>Delete</button>
                      <a className="ae-btn" href={`${apiBase}/clients/${selected.id}/documents/${d.id}/download`}>Download</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function DocumentsModal({ client, apiBase, onClose }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const inputRef = useRef(null);
  

  const base = `${apiBase}/clients/${client.id}/documents`;

  const load = async () => {
    const r = await fetch(base, { credentials:'include' });
    const j = await r.json();
const docsArr = Array.isArray(j) ? j : (j.docs || []);
setDocs(docsArr);

  };

  useEffect(()=>{ load(); }, []); // initial

  const upload = async (e) => {
    e.preventDefault();
    if (!inputRef.current?.files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of inputRef.current.files) fd.append('files', f);
      fd.append('meta', JSON.stringify({ title, notes }));
     const resp = await fetch(base, { method:'POST', body: fd, credentials:'include' });
const data = await resp.json();
setTitle(''); setNotes('');
if (inputRef.current) inputRef.current.value = '';
await load();

    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    setBusy(true);
    try {
      await fetch(`${base}/${id}`, { method:'DELETE', credentials:'include' });
      await load();
    } finally { setBusy(false); }
  };

  const saveMeta = async (id, patch) => {
    setBusy(true);
    try {
      await fetch(`${base}/${id}`, {
        method:'PATCH',
        headers:{ 'Content-Type':'application/json' },
        credentials:'include',
        body: JSON.stringify(patch),
      });
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="ae-modal">
      <div className="ae-modal-card ae-docs-modal">
        <div className="ae-modal-head">
          <div>
            <h3 style={{margin:0}}>Documents — #{client.id}</h3>
            <div className="ae-muted" style={{marginTop:4}}>
              {client.name || 'Unnamed'} {client.email ? `· ${client.email}` : ''}
            </div>
          </div>
          <button className="ae-btn ae-ghost" onClick={onClose}>Close</button>
        </div>

        <form className="ae-docs-upload" onSubmit={upload}>
          <input ref={inputRef} type="file" multiple />
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Title (optional, applied to all)" />
          <input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Notes (optional, applied to all)" />
          <button className="ae-btn ae-primary" disabled={busy} type="submit">
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </form>

        <div className="ae-docs-list">
          {docs.length === 0 ? (
            <div className="ae-muted">No documents yet.</div>
          ) : docs.map(d=>(
            <div className="ae-doc-row" key={d.id}>
              <div className="ae-doc-main">
                <a className="ae-doc-title" href={`${base}/${d.id}/download`} title="Download">
                  {d.title || d.originalName}
                </a>
                <div className="ae-doc-meta ae-muted">
                  {fmtSize(d.size)} · {new Date(d.uploadedAt).toLocaleString()} · {d.mime || 'file'}
                </div>
                <div className="ae-doc-notes">
                  <span className="ae-muted">Notes:</span>{' '}
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={e=>saveMeta(d.id, { notes: e.currentTarget.textContent })}
                  >
                    {d.notes || 'Add notes…'}
                  </span>
                </div>
              </div>
              <div className="ae-doc-actions">
                <button className="ae-btn ae-ghost"
                        onClick={()=>{
                          const t = prompt('Title', d.title || d.originalName || '');
                          if (t !== null) saveMeta(d.id, { title: t });
                        }}>
                  Rename
                </button>
                <button className="ae-btn ae-ghost" onClick={()=>del(d.id)}>Delete</button>
                <a className="ae-btn" href={`${base}/${d.id}/download`}>Download</a>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}


 return (
  <div className="ae-wrap">
    {/* TAB SWITCHER */}
    <div className="ae-tabs">
      <button
        className={`ae-tab ${activeTab === 'overview' ? 'is-active' : ''}`}
        onClick={() => setActiveTab('overview')}
      >
        Overview
      </button>
   

      <button
        className={`ae-tab ${activeTab === 'docs' ? 'is-active' : ''}`}
        onClick={() => setActiveTab('docs')}
      >
        Documents Center
      </button>
    </div>
    

    {/* ======================= OVERVIEW TAB ======================= */}
    {activeTab === 'overview' ? (
      <>
        <section className="ae-card">
          <h2>📊 Sign-Up Analytics</h2>
          <ul className="ae-stats">
            <li><div className="ae-muted">Today</div><strong>{stats.todayCount}</strong></li>
            <li><div className="ae-muted">Yesterday</div><strong>{stats.yesterdayCount}</strong></li>
            <li><div className="ae-muted">Last 7 days</div><strong>{stats.weekCount}</strong></li>
            <li><div className="ae-muted">Last 30 days</div><strong>{stats.monthCount}</strong></li>
            <li><div className="ae-muted">GA4 Active (7d)</div><strong>{stats.gaActive}</strong></li>
          </ul>
        </section>

       <section className="ae-card">
  <div className="ae-card-title-row" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12}}>
    <h2>👥 Client Cases</h2>
    <button
      type="button"
      className="ae-btn ae-ghost ae-collapse-btn"
      onClick={() => setClientCasesCollapsed(v => !v)}
    >
      {clientCasesCollapsed ? 'Show' : 'Minimize'}
    </button>
  </div>

  {/* Search/filter bar (kept outside the collapse so you can unhide quickly) */}
  <div className="ae-toolbar" style={{marginBottom: clientCasesCollapsed ? 0 : 12}}>
    <input
      type="text"
      placeholder="Filter clients by name or email…"
      value={clientFilter}
      onChange={(e)=>setClientFilter(e.target.value)}
    />
  </div>

  {clientCasesCollapsed ? (
    <div className="ae-collapsed-note">
      <span>
        {
          clients.filter(c => {
            const t = clientFilter.toLowerCase();
            const s = `${c.firstName||''} ${c.lastName||''} ${c.email||''}`.toLowerCase();
            return s.includes(t);
          }).length
        } matches
      </span>
      <span className="ae-dot">•</span>
      <span>Table hidden</span>
    </div>
  ) : (
    <table className="ae-table ae-table-editable">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Stage</th>
          <th>Last Follow-Up</th>
          <th>Notes</th>
          <th>Last Email</th>
          <th>Docs</th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        {clients
          .filter(c => {
            const t = clientFilter.toLowerCase();
            const s = `${c.firstName||''} ${c.lastName||''} ${c.email||''}`.toLowerCase();
            return s.includes(t);
          })
          .map(c => (
          <tr key={c.id}>
            <td data-label="Name">{c.firstName} {c.lastName}</td>
            <td data-label="Email">{c.email}</td>

            {/* Stage */}
            <td data-label="Stage">
              <input
                className="ae-inline-input"
                defaultValue={c.pipelineStage || ''}
                placeholder="Won / Negotiation / New"
                onBlur={async (e) => {
                  const v = e.target.value.trim() || null;
                  updateClientLocal(c.id, { pipelineStage: v });
                  try {
                    await fetch(`${apiBase}/clients/${c.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ pipelineStage: v })
                    });
                  } catch (err) { console.error('save stage failed', err); }
                }}
              />
            </td>

            {/* Last Follow-Up */}
            <td data-label="Last Follow-Up">
              <input
                type="date"
                className="ae-inline-input"
                defaultValue={toYMD(c.lastFollowUp)}
                onBlur={async (e) => {
                  const v = e.target.value || null;
                  updateClientLocal(c.id, { lastFollowUp: v });
                  try {
                    await fetch(`${apiBase}/clients/${c.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ lastFollowUp: v })
                    });
                  } catch (err) { console.error('save lastFollowUp failed', err); }
                }}
              />
            </td>

            {/* Notes */}
            <td data-label="Notes">
              <input
                className="ae-inline-input"
                defaultValue={c.followUpNotes || ''}
                placeholder="Short note"
                onBlur={async (e) => {
                  const v = e.target.value.trim() || null;
                  updateClientLocal(c.id, { followUpNotes: v });
                  try {
                    await fetch(`${apiBase}/clients/${c.id}`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ followUpNotes: v })
                    });
                  } catch (err) { console.error('save notes failed', err); }
                }}
              />
            </td>

            <td data-label="Last Email">
              {c.lastEmailSentAt ? new Date(c.lastEmailSentAt).toLocaleString() : '—'}
            </td>

            <td data-label="Docs"><span className="ae-pill">{c.documentsCount || 0}</span></td>

            <td data-label="Actions" className="ae-right">
              <button className="ae-btn" onClick={()=>openComposeForUser(c)}>Email</button>{' '}
              <button
                className="ae-btn ae-ghost"
                onClick={()=>{
                  setDocsClient({
                    id: c.id,
                    name: `${c.firstName||''} ${c.lastName||''}`.trim(),
                    email: c.email
                  });
                  setDocsOpen(true);
                }}
              >
                Documents
              </button>
            </td>
          </tr>
        ))}
        
      </tbody>
    </table>
  )}
</section>


             

        <section className="ae-card">
          <h2>🗞️ Daily Executive Summary</h2>
          <div className="ae-toolbar">
            <input
              type="text"
              value={q}
              placeholder="Search users by name or email…"
              onChange={(e)=>setQ(e.target.value)}
              onKeyDown={(e)=>{ if (e.key==='Enter') runSearch(); }}
            />
            <button className="ae-btn" onClick={runSearch} disabled={loadingUsers}>
              {loadingUsers ? 'Loading users…' : 'Search'}
            </button>
          </div>

          {results.length>0 && (
            <div className="ae-search-results">
              {results.map(u=>(
                <div className="ae-search-row" key={u.id}>
                  <div><span className="ae-pill">#{u.id}</span>{u.firstName} {u.lastName} — {u.email}</div>
                  <div>
                    <button className="ae-btn" onClick={()=>addTo('converted',u)}>+ Converted</button>{' '}
                    <button className="ae-btn" onClick={()=>addTo('pipeline',u)}>+ Pipeline</button>{' '}
                    <button className="ae-btn" onClick={()=>addTo('leads',u)}>+ Lead</button>{' '}
                    <button className="ae-btn" onClick={()=>openComposeForUser(u)}>Email</button>{' '}
                    <button
                      className="ae-btn ae-ghost"
                      onClick={()=>{
                        setDocsClient({
                          id: u.id,
                          name: `${u.firstName||''} ${u.lastName||''}`.trim(),
                          email: u.email
                        });
                        setDocsOpen(true);
                      }}
                    >
                      Documents
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Section title="Converted" name="converted" />
          <Section title="Pipeline"  name="pipeline" />
          <Section title="Leads"     name="leads" />

          <div className="ae-card">
            <h3>Recipients</h3>
            <input
              type="text"
              placeholder="comma,separated@emails"
              value={(draft.recipients||[]).join(',')}
              onChange={(e)=>setDraft(d=>({...d, recipients:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))}
            />
            <div className="ae-actions">
              <button className="ae-btn" onClick={saveDraft} disabled={saving}>{saving ? 'Saving…' : 'Save Draft'}</button>
              <button className="ae-btn ae-primary" onClick={sendNow} disabled={sending}>{sending ? 'Sending…' : 'Send Now'}</button>
            </div>
          </div>
        </section>

        {composeOpen && (
          <div className="ae-modal">
            <div className="ae-modal-card">
              <h3>Email Client</h3>
              <label>To</label>
              <input type="email" value={composeTo} onChange={(e)=>setComposeTo(e.target.value)} />
              <label>Subject</label>
              <input type="text" value={composeSubject} onChange={(e)=>setComposeSubject(e.target.value)} />
              <label>Message</label>
              <textarea rows={8} value={composeBody} onChange={(e)=>setComposeBody(e.target.value)} />
              <div className="ae-actions">
                <button className="ae-btn ae-ghost" onClick={()=>setComposeOpen(false)}>Cancel</button>
                <button
                  className="ae-btn ae-primary"
                  disabled={sendingClientEmail}
                  onClick={async ()=>{
                    try {
                      setSendingClientEmail(true);
                      const res = await fetch(`${apiBase}/email-client`, {
                        method:'POST',
                        headers:{ 'Content-Type':'application/json' },
                        credentials:'include',
                        body: JSON.stringify({
                          userId: composeUserId,
                          to: composeTo || undefined,
                          subject: composeSubject,
                          html: `<div style="font-family:Arial,Helvetica,sans-serif">${composeBody.replace(/\n/g,'<br/>')}</div>`
                        })
                      });
                      const data = await res.json();
                      if (!data.ok) throw new Error(data.error || 'Failed to send');

                      // Optimistically stamp lastEmailSentAt in UI
                      updateClientLocal(composeUserId, { lastEmailSentAt: new Date().toISOString() });

                      alert('Email sent!');
                      setComposeOpen(false);
                    } catch (e) {
                      alert(e.message);
                    } finally {
                      setSendingClientEmail(false);
                    }
                  }}
                >
                  {sendingClientEmail ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
          
        )}
        
{/* 
        {docsOpen && docsClient && (
          <DocumentsModal
            client={docsClient}
            apiBase={apiBase}
            onClose={()=>{ setDocsOpen(false); setDocsClient(null); }}
          />
        )} */}
        
      </>
    ) : (
      <DocumentsCenter
        apiBase={apiBase}
        openModalFor={(client) => { setDocsClient(client); setDocsOpen(true); }}
      />
    )}

    {/* 👇 Render the Docs modal ONCE, outside the tab body */}
    {docsOpen && docsClient && (
      <DocumentsModal
        client={docsClient}
        apiBase={apiBase}
        onClose={() => { setDocsOpen(false); setDocsClient(null); }}
      />
    )}
  </div>
);



}

